import React, { useState } from "react";
import { toast } from "sonner";
import { Users, Plus, Trash2, GraduationCap, BookOpen, Heart, ArrowLeft } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCollection } from "@/hooks/useCollection";
import { api } from "@/lib/api";
import ProGate from "@/components/ProGate";

const AVATAR_COLORS = ["#8FA9E3", "#6FC2B0", "#EE9C86", "#B69CE0", "#78B7E0", "#E3B7D9"];

export default function Students() {
  return (
    <ProGate title="Student Tracking is a Pro feature" description="Keep gentle academic and behavior notes for your class. Upgrade to unlock.">
      <StudentsInner />
    </ProGate>
  );
}

function StudentsInner() {
  const students = useCollection("students");
  const logs = useCollection("student-logs");
  const [selected, setSelected] = useState(null);
  const [addStudentOpen, setAddStudentOpen] = useState(false);
  const [studentForm, setStudentForm] = useState({ name: "", grade: "", class_name: "", notes: "" });
  const [logForm, setLogForm] = useState({ log_type: "academic", title: "", note: "", score: "", date: new Date().toISOString().slice(0, 10) });
  const [logOpen, setLogOpen] = useState(false);

  const addStudent = async () => {
    if (!studentForm.name.trim()) return toast.error("Add a name");
    await students.create(studentForm);
    toast.success("Student added");
    setStudentForm({ name: "", grade: "", class_name: "", notes: "" });
    setAddStudentOpen(false);
  };

  const addLog = async () => {
    if (!logForm.title.trim()) return toast.error("Add a title");
    await logs.create({ ...logForm, student_id: selected.id });
    toast.success("Log added");
    setLogForm({ log_type: "academic", title: "", note: "", score: "", date: new Date().toISOString().slice(0, 10) });
    setLogOpen(false);
  };

  const studentLogs = (sid) => logs.items.filter((l) => l.student_id === sid).sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  // Detail view
  if (selected) {
    const s = students.items.find((x) => x.id === selected.id) || selected;
    const sLogs = studentLogs(s.id);
    const academic = sLogs.filter((l) => l.log_type === "academic");
    const behavior = sLogs.filter((l) => l.log_type === "behavior");
    return (
      <div>
        <button onClick={() => setSelected(null)} className="flex items-center gap-1.5 text-slate-500 hover:text-primary mb-5 text-sm" data-testid="back-to-students"><ArrowLeft size={16} /> All students</button>
        <div className="flex items-center gap-4 mb-8">
          <span className="h-16 w-16 rounded-3xl flex items-center justify-center text-white text-2xl font-heading font-extrabold" style={{ background: AVATAR_COLORS[0] }}>{s.name[0]?.toUpperCase()}</span>
          <div>
            <h1 className="font-heading text-3xl font-extrabold text-slate-800">{s.name}</h1>
            <p className="text-slate-500">{[s.grade, s.class_name].filter(Boolean).join(" · ")}</p>
          </div>
          <Button onClick={() => setLogOpen(true)} data-testid="add-log-button" className="rounded-full bg-primary hover:bg-primary/90 ml-auto"><Plus size={18} className="mr-1" /> Add log</Button>
        </div>
        {s.notes && <div className="tcc-card p-5 mb-6"><p className="text-sm text-slate-600">{s.notes}</p></div>}

        <div className="grid md:grid-cols-2 gap-6">
          <LogColumn title="Academic" icon={BookOpen} logs={academic} onDelete={logs.remove} color="#8FA9E3" />
          <LogColumn title="Behavior & Intervention" icon={Heart} logs={behavior} onDelete={logs.remove} color="#EE9C86" />
        </div>

        <Dialog open={logOpen} onOpenChange={setLogOpen}>
          <DialogContent className="rounded-3xl" data-testid="log-dialog">
            <DialogHeader><DialogTitle className="font-heading">New log for {s.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Type</Label>
                <Select value={logForm.log_type} onValueChange={(v) => setLogForm({ ...logForm, log_type: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="log-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="academic">Academic</SelectItem><SelectItem value="behavior">Behavior / Intervention</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Title</Label><Input value={logForm.title} onChange={(e) => setLogForm({ ...logForm, title: e.target.value })} data-testid="log-title-input" className="rounded-xl mt-1" placeholder="Quiz 3 / Note" /></div>
                <div><Label>Score / Level</Label><Input value={logForm.score} onChange={(e) => setLogForm({ ...logForm, score: e.target.value })} className="rounded-xl mt-1" placeholder="88% / Tier 2" /></div>
              </div>
              <div><Label>Date</Label><Input type="date" value={logForm.date} onChange={(e) => setLogForm({ ...logForm, date: e.target.value })} className="rounded-xl mt-1" /></div>
              <div><Label>Note</Label><Textarea value={logForm.note} onChange={(e) => setLogForm({ ...logForm, note: e.target.value })} className="rounded-xl mt-1" rows={3} /></div>
            </div>
            <DialogFooter><Button onClick={addLog} data-testid="log-save-button" className="rounded-full bg-primary hover:bg-primary/90">Add log</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List view
  return (
    <div>
      <PageHeader
        title="Students" icon={Users}
        subtitle="Lightweight academic and behavior notes — simple, never overwhelming."
        action={<Button onClick={() => setAddStudentOpen(true)} data-testid="add-student-button" className="rounded-full bg-primary hover:bg-primary/90"><Plus size={18} className="mr-1" /> Add student</Button>}
      />

      {students.loading ? <p className="text-slate-400">Loading…</p> : students.items.length === 0 ? (
        <div className="tcc-card p-12 text-center">
          <GraduationCap className="mx-auto text-primary/50 mb-3" size={40} strokeWidth={1.4} />
          <p className="font-heading font-bold text-slate-700 text-lg">No students yet</p>
          <p className="text-slate-400 text-sm mt-1 mb-5">Add students to keep gentle track of progress and interventions.</p>
          <Button onClick={() => setAddStudentOpen(true)} className="rounded-full bg-primary hover:bg-primary/90"><Plus size={18} className="mr-1" /> Add student</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {students.items.map((s, i) => {
            const count = studentLogs(s.id).length;
            return (
              <button key={s.id} onClick={() => setSelected(s)} data-testid="student-card"
                className="tcc-card p-5 text-left flex items-center gap-4 hover:-translate-y-1 transition-transform duration-200 group">
                <span className="h-[52px] w-[52px] rounded-2xl flex items-center justify-center text-white text-lg font-heading font-extrabold shrink-0" style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>{s.name[0]?.toUpperCase()}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold text-slate-800 truncate">{s.name}</p>
                  <p className="text-xs text-slate-400">{[s.grade, s.class_name].filter(Boolean).join(" · ") || "—"}</p>
                  <p className="text-xs text-primary mt-1">{count} log{count !== 1 ? "s" : ""}</p>
                </div>
                <span onClick={(e) => { e.stopPropagation(); students.remove(s.id); }} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-destructive transition"><Trash2 size={16} /></span>
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={addStudentOpen} onOpenChange={setAddStudentOpen}>
        <DialogContent className="rounded-3xl" data-testid="student-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add student</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={studentForm.name} onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })} data-testid="student-name-input" className="rounded-xl mt-1" placeholder="Alex Johnson" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Grade</Label><Input value={studentForm.grade} onChange={(e) => setStudentForm({ ...studentForm, grade: e.target.value })} className="rounded-xl mt-1" placeholder="5th" /></div>
              <div><Label>Class</Label><Input value={studentForm.class_name} onChange={(e) => setStudentForm({ ...studentForm, class_name: e.target.value })} className="rounded-xl mt-1" placeholder="Homeroom" /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={studentForm.notes} onChange={(e) => setStudentForm({ ...studentForm, notes: e.target.value })} className="rounded-xl mt-1" rows={2} /></div>
          </div>
          <DialogFooter><Button onClick={addStudent} data-testid="student-save-button" className="rounded-full bg-primary hover:bg-primary/90">Add student</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LogColumn({ title, icon: Icon, logs, onDelete, color }) {
  return (
    <div className="tcc-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: `${color}22` }}><Icon size={18} style={{ color }} strokeWidth={1.7} /></div>
        <h3 className="font-heading font-bold text-slate-800">{title}</h3>
      </div>
      {logs.length === 0 ? <p className="text-sm text-slate-400 py-3 text-center">No logs yet.</p> : (
        <div className="space-y-2.5">
          {logs.map((l) => (
            <div key={l.id} className="p-3 rounded-2xl bg-slate-50 group relative" data-testid="student-log-item">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-800 text-sm">{l.title}</p>
                {l.score && <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: `${color}22`, color }}>{l.score}</span>}
              </div>
              {l.note && <p className="text-xs text-slate-500 mt-1">{l.note}</p>}
              <p className="text-[11px] text-slate-400 mt-1">{l.date}</p>
              <button onClick={() => onDelete(l.id)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-slate-300 hover:text-destructive transition"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
